// M5.2 — persistent quest tracker docked top-right. Lists active and
// completable quests so the player sees objective progress without re-opening
// the NPC dialog. Auto-hides when there's nothing to show. Completed
// (turned-in) quests are filtered out — the tracker is "what's ahead", not
// "what you've done".

import {
  getMobTemplate,
  getQuestTemplate,
  type QuestLogEntry,
  type QuestObjective,
  type QuestTemplate,
} from "@vinyl/shared";
import { el } from "./dom.js";

export class QuestTracker {
  readonly root: HTMLElement;
  private entries: QuestLogEntry[] = [];

  constructor() {
    this.root = el("aside", { class: "quest-tracker hidden" });
  }

  setEntries(entries: QuestLogEntry[]): void {
    this.entries = entries.filter((e) => e.status !== "completed");
    this.render();
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    this.root.replaceChildren();
    if (this.entries.length === 0) {
      this.root.classList.add("hidden");
      return;
    }
    this.root.classList.remove("hidden");
    this.root.append(el("h4", { class: "quest-tracker-title" }, "Quests"));
    for (const entry of this.entries) {
      const tpl = getQuestTemplate(entry.questId);
      if (!tpl) continue;
      const row = el("div", { class: "quest-tracker-row" });
      row.append(el("div", { class: "quest-tracker-name" }, tpl.name));
      for (let i = 0; i < tpl.objectives.length; i++) {
        const obj = tpl.objectives[i]!;
        const have = entry.progress[i] ?? 0;
        const line = this.objectiveLine(tpl, obj, have);
        const cls =
          have >= obj.count
            ? "quest-tracker-objective quest-tracker-objective-done"
            : "quest-tracker-objective";
        row.append(el("div", { class: cls }, line));
      }
      if (entry.status === "completable") {
        row.append(el("div", { class: "quest-tracker-ready" }, "Ready to turn in!"));
      }
      this.root.append(row);
    }
  }

  private objectiveLine(_tpl: QuestTemplate, obj: QuestObjective, have: number): string {
    if (obj.kind === "kill") {
      const mob = getMobTemplate(obj.mobTemplateId);
      const name = mob ? mob.name : obj.mobTemplateId;
      const plural = obj.count > 1 ? "s" : "";
      return `${Math.min(have, obj.count)} / ${obj.count} ${name}${plural}`;
    }
    return "";
  }
}
